/**
 * Shows a banner on sites whose plan limits the number of team members, once they reach it.
 *
 * Owners and billing account managers are the only ones the server sends
 * org.billableMemberCount, and they are the only ones with anything to do about it, so they
 * are the only ones shown anything: a nag with an upgrade link, dismissible (per user and
 * site) until the site drops below the limit and reaches it again.
 *
 * Upgrading is the remedy offered in every case, here and on the read-only banner below.
 *
 * A site over its limit is held read-only, and SiteReadOnlyBanner says so; this banner stands
 * down for it. Being over is not a state this banner has to warn about, since members past
 * the limit cannot be added: a site is only ever over because it was already over when the
 * limit arrived, and it goes read-only the moment it is.
 */

import { Banner, buildBannerMessage } from "app/client/components/Banner";
import { buildUpgradeMessage } from "app/client/components/DocumentUsage";
import { makeT } from "app/client/lib/localization";
import { AppModel } from "app/client/models/AppModel";
import { getUserOrgPrefObs, getUserOrgPrefsObs } from "app/client/models/UserPrefs";
import { canUpgradeOrg } from "app/common/roles";

import { Disposable, dom, makeTestId, Observable } from "grainjs";

const t = makeT("SiteUserLimitBanner");

const testId = makeTestId("test-site-user-limit-banner-");

export class SiteUserLimitBanner extends Disposable {
  private readonly _currentOrg = this._app.currentOrg;
  private readonly _limit = this._currentOrg?.billingAccount?.features?.maxUsersPerOrg;
  private readonly _count = this._currentOrg?.billableMemberCount;

  // Whether the reader is someone who can lift the limit themselves, rather than someone who
  // has to ask. Billing managers can, whatever their access to the site. This is the same set
  // the server sends the count to.
  private readonly _canAct = this._app.isOwner() ||
    Boolean(this._currentOrg?.billingAccount?.isManager);

  // Dismissal is per user and org, so dismissing on one site does not silence another.
  private _dismissed: Observable<boolean | undefined> = this.autoDispose(
    getUserOrgPrefObs(
      this.autoDispose(getUserOrgPrefsObs(this._app)), "dismissedUserLimitBanner"));

  constructor(private _app: AppModel) {
    super();
  }

  public buildDom() {
    const limit = this._limit;
    const count = this._count;
    if (limit === undefined) { return null; }
    // SiteReadOnlyBanner has taken over; see the header.
    if (this._currentOrg?.readOnlyReason) { return null; }
    if (!this._canAct || count === undefined) { return null; }
    // A dismissal covers the site being at its limit now, not the next time it gets there.
    if (count < limit && this._dismissed.get()) { this._dismissed.set(undefined); }
    if (count === limit) { return this._buildAtLimitBanner(limit); }
    return null;
  }

  private _buildAtLimitBanner(limit: number) {
    return dom.domComputed((use) => {
      if (use(this._dismissed)) { return null; }

      return dom.create(Banner, {
        content: buildBannerMessage(
          t("Your site has reached the free plan limit of {{count}} team members.", { count: limit }),
          " ",
          this._buildUpgradeMessage(),
          testId("text"),
        ),
        contentSmall: buildBannerMessage(this._buildUpgradeMessage()),
        style: "warning",
        showCloseButton: true,
        showExpandButton: true,
        onClose: () => this._dismissed.set(true),
      });
    });
  }

  private _buildUpgradeMessage() {
    return buildUpgradeMessage(
      this._app,
      "siteUserLimitBanner",
      canUpgradeOrg(this._currentOrg),
      "long",
    );
  }
}
